import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Modal, Button, Form, message, Input } from "antd";
import { LinkOutlined } from "@ant-design/icons";

import { GRAPH_QUERY_BASEURL, GRAPH_V2_QUERY_BASEURL } from "../../config/StaticConfig";
import { subgraphDetailPath, subgraphV2DetailPath } from "../../helpers/paths";
import { CREATE_SUBGRAPH_MUTATION, SUBGRAPHS_QUERY } from "../../helpers/graphql/subgraph";

interface Project {
  id: string;
  name: string;
  identifier: string;
  secret: string;
}

interface Props {
  project: Project;
  cluster: string;
}

interface SubgraphFormValues {
  name: string;
  identifier: string;
}

export function CreateSubgraph({ project, cluster }: Props) {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [createSubgraph, { loading }] = useMutation(CREATE_SUBGRAPH_MUTATION, {
    onCompleted: ({ createSubgraph }) => {
      if (createSubgraph) {
        message.success("Subgraph created!");
        navigate(cluster === 'v1' ?
          subgraphDetailPath(createSubgraph.projectId, createSubgraph.identifier) :
          subgraphV2DetailPath(createSubgraph.projectId, createSubgraph.identifier)
        );
      }
    },

    refetchQueries: ({ data }) => [
      {
        query: SUBGRAPHS_QUERY,
        variables: {
          projectId: data.createSubgraph.projectId,
          cluster
        },
      },
    ],
  });

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: SubgraphFormValues) => {
    createSubgraph({
      variables: {
        ...values,
        projectId: project.id,
        cluster,
      },
    });
  };

  const handleValuesChange = async (changedValues: any) => {
    if (changedValues.name !== undefined) {
      form.setFieldsValue({ identifier: changedValues.name.replace(/\s+/g, "-").toLowerCase() });
    }

    if (changedValues.identifier !== undefined) {
      form.setFieldsValue({ identifier: changedValues.identifier.toLowerCase() });
    }
  };

  const graphQueryBaseUrl = cluster === 'v1' ? GRAPH_QUERY_BASEURL : GRAPH_V2_QUERY_BASEURL;

  return (
    <>
      <Button type="primary" onClick={showModal}>
        Create Subgraph
      </Button>

      <Modal
        title="Create Subgraph"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button onClick={handleCancel} key="back">
            Cancel
          </Button>,
          <Button
            form="createSubgraphForm"
            key="submit"
            htmlType="submit"
            type="primary"
            loading={loading}
          >
            Create
          </Button>,
        ]}
      >
        <Form<SubgraphFormValues>
          id="createSubgraphForm"
          name="createSubgraph"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
          autoComplete="off"
        >
          <Form.Item name="name" label="Subgraph Name" required>
            <Input />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.identifier !== currentValues.identifier
            }
          >
            {({ getFieldValue }: any) => (
              <Form.Item
                name="identifier"
                label={"Subgraph Identifier"}
                required
                extra={
                  <div style={{ marginTop: "5px" }}>
                    <LinkOutlined /> {graphQueryBaseUrl}/{project.identifier}/
                    <strong>{getFieldValue("identifier")}</strong>
                  </div>
                }
              >
                <Input placeholder="Lowercase and hyphen only" />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
